/**
 * Commits Service
 * Handles commit tracking operations for git commits saved to Firebase
 */
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  where,
  limit,
  startAfter,
  updateDoc,
  setDoc,
  Timestamp,
} from 'firebase/firestore'
import { getFirebaseDb } from '../firebase/firebase.js'
import logger from './logger.js'

const log = logger.createScoped('CommitsService')

// Collection name
const COMMITS_COLLECTION = 'Commits'

/**
 * Get paginated commits ordered by date descending
 * @param {number} limitCount - Number of commits to fetch (default: 25)
 * @param {Timestamp} lastDate - Last commit date for pagination (optional)
 * @returns {Promise<Array>} Array of commit documents
 */
export async function getCommits(limitCount = 25, lastDate = null) {
  log.debug('Fetching commits', { limitCount, lastDate })

  try {
    const db = getFirebaseDb()
    const commitsRef = collection(db, COMMITS_COLLECTION)

    let q = query(commitsRef, orderBy('date', 'desc'), limit(limitCount))

    // Add pagination if lastDate provided
    if (lastDate) {
      q = query(commitsRef, orderBy('date', 'desc'), startAfter(lastDate), limit(limitCount))
    }

    const snapshot = await getDocs(q)
    const commits = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }))

    log.debug('Commits fetched', { count: commits.length })
    return commits
  } catch (error) {
    log.error('Failed to fetch commits', error)
    throw error
  }
}

/**
 * Get a single commit by ID (commit hash)
 * @param {string} commitId - The commit hash (document ID)
 * @returns {Promise<Object|null>} Commit document or null if not found
 */
export async function getCommitById(commitId) {
  log.debug('Fetching commit by ID', { commitId })

  try {
    const db = getFirebaseDb()
    const commitRef = doc(db, COMMITS_COLLECTION, commitId)
    const commitSnap = await getDoc(commitRef)

    if (!commitSnap.exists()) {
      log.debug('Commit not found', { commitId })
      return null
    }

    const commit = {
      id: commitSnap.id,
      ...commitSnap.data(),
    }

    log.debug('Commit found', { commitId })
    return commit
  } catch (error) {
    log.error('Failed to fetch commit by ID', error)
    throw error
  }
}

/**
 * Search commits by text in subject, body, or author
 * @param {string} searchQuery - Text to search for
 * @param {number} limitCount - Max results (default: 50)
 * @returns {Promise<Array>} Array of matching commit documents
 */
export async function searchCommits(searchQuery, limitCount = 50) {
  log.debug('Searching commits', { searchQuery, limitCount })

  if (!searchQuery || !searchQuery.trim()) {
    return []
  }

  try {
    const db = getFirebaseDb()
    const commitsRef = collection(db, COMMITS_COLLECTION)

    // Firebase doesn't support full-text search, so we'll fetch all and filter client-side
    // For better performance, consider using Algolia or similar in production
    const q = query(commitsRef, orderBy('date', 'desc'), limit(limitCount * 2))

    const snapshot = await getDocs(q)
    const searchLower = searchQuery.toLowerCase().trim()

    const commits = snapshot.docs
      .map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }))
      .filter((commit) => {
        const subjectMatch = commit.subject?.toLowerCase().includes(searchLower)
        const bodyMatch = commit.body?.toLowerCase().includes(searchLower)
        const authorMatch = commit.author?.toLowerCase().includes(searchLower)
        return subjectMatch || bodyMatch || authorMatch
      })
      .slice(0, limitCount)

    log.debug('Commits search completed', { count: commits.length })
    return commits
  } catch (error) {
    log.error('Failed to search commits', error)
    throw error
  }
}

/**
 * Get commit statistics
 * @returns {Promise<Object>} Statistics object with counts and totals
 */
export async function getCommitsStats() {
  log.debug('Calculating commits statistics')

  try {
    const db = getFirebaseDb()
    const commitsRef = collection(db, COMMITS_COLLECTION)

    // Get all commits (consider adding caching for large datasets)
    const snapshot = await getDocs(commitsRef)
    const commits = snapshot.docs.map((doc) => doc.data())

    // Calculate statistics
    const stats = {
      totalCommits: commits.length,
      totalLinesAdded: 0,
      totalLinesDeleted: 0,
      totalLinesChanged: 0,
      totalFiles: 0,
      avgLinesPerCommit: 0,
      commitsThisMonth: 0,
      commitsThisWeek: 0,
      emailsSent: 0,
    }

    const now = new Date()
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
    const startOfWeek = new Date(now)
    startOfWeek.setDate(now.getDate() - now.getDay())

    commits.forEach((commit) => {
      // Code statistics
      if (commit.stats) {
        stats.totalLinesAdded += commit.stats.linesAdded || 0
        stats.totalLinesDeleted += commit.stats.linesDeleted || 0
        stats.totalFiles += commit.stats.filesChanged || 0
      }

      // Date-based counts
      const commitDate = commit.date?.toDate ? commit.date.toDate() : new Date(commit.dateISO)
      if (commitDate >= startOfMonth) {
        stats.commitsThisMonth++
      }
      if (commitDate >= startOfWeek) {
        stats.commitsThisWeek++
      }

      // Email tracking
      if (commit.emailSent) {
        stats.emailsSent++
      }
    })

    stats.totalLinesChanged = stats.totalLinesAdded + stats.totalLinesDeleted
    stats.avgLinesPerCommit =
      stats.totalCommits > 0 ? Math.round(stats.totalLinesChanged / stats.totalCommits) : 0

    log.debug('Statistics calculated', stats)
    return stats
  } catch (error) {
    log.error('Failed to calculate statistics', error)
    throw error
  }
}

/**
 * Mark a commit as having email sent
 * @param {string} commitId - The commit hash (document ID)
 * @param {string} emailDocId - The email document ID from Emails collection (optional)
 * @returns {Promise<void>}
 */
export async function markCommitEmailSent(commitId, emailDocId = null) {
  log.info('Marking commit email as sent', { commitId, emailDocId })

  try {
    const db = getFirebaseDb()
    const commitRef = doc(db, COMMITS_COLLECTION, commitId)

    await updateDoc(commitRef, {
      emailSent: true,
      emailDocId: emailDocId,
      emailSentAt: Timestamp.now(),
    })

    log.info('Commit email marked as sent', { commitId })
  } catch (error) {
    log.error('Failed to mark commit email as sent', error)
    throw error
  }
}

/**
 * Subscribe to realtime updates of commits collection
 * @param {Function} callback - Callback receiving array of commit docs and optional error
 * @param {Object} options - Optional filters and settings
 * @param {number} options.limit - Max number of commits (default: 50)
 * @param {string} options.branch - Filter by branch (optional)
 * @returns {Function} Unsubscribe function
 */
export function subscribeCommits(callback, options = {}) {
  log.debug('Subscribing to commits collection', options)

  const db = getFirebaseDb()
  const commitsRef = collection(db, COMMITS_COLLECTION)

  // Build query with filters
  let q = query(commitsRef, orderBy('date', 'desc'))

  // Add branch filter if specified
  if (options.branch) {
    q = query(q, where('branch', '==', options.branch))
  }

  // Add limit
  const limitCount = options.limit || 50
  q = query(q, limit(limitCount))

  const unsubscribe = onSnapshot(
    q,
    (snapshot) => {
      const commits = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }))

      log.debug('Commits updated', { count: commits.length })
      callback(commits, null)
    },
    (error) => {
      log.error('Commits subscription error', error)
      callback([], error)
    }
  )

  return unsubscribe
}

/**
 * Get commits filtered by date range
 * @param {Date} startDate - Start date (inclusive)
 * @param {Date} endDate - End date (inclusive)
 * @param {number} limitCount - Max results (default: 100)
 * @returns {Promise<Array>} Array of commit documents
 */
export async function getCommitsByDateRange(startDate, endDate, limitCount = 100) {
  log.debug('Fetching commits by date range', { startDate, endDate, limitCount })

  try {
    const db = getFirebaseDb()
    const commitsRef = collection(db, COMMITS_COLLECTION)

    const startTimestamp = Timestamp.fromDate(startDate)
    const endTimestamp = Timestamp.fromDate(endDate)

    const q = query(
      commitsRef,
      where('date', '>=', startTimestamp),
      where('date', '<=', endTimestamp),
      orderBy('date', 'desc'),
      limit(limitCount)
    )

    const snapshot = await getDocs(q)
    const commits = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }))

    log.debug('Commits by date range fetched', { count: commits.length })
    return commits
  } catch (error) {
    log.error('Failed to fetch commits by date range', error)
    throw error
  }
}

/**
 * Get commits by author
 * @param {string} authorName - Author name to filter by
 * @param {number} limitCount - Max results (default: 50)
 * @returns {Promise<Array>} Array of commit documents
 */
export async function getCommitsByAuthor(authorName, limitCount = 50) {
  log.debug('Fetching commits by author', { authorName, limitCount })

  try {
    const db = getFirebaseDb()
    const commitsRef = collection(db, COMMITS_COLLECTION)

    const q = query(
      commitsRef,
      where('author', '==', authorName),
      orderBy('date', 'desc'),
      limit(limitCount)
    )

    const snapshot = await getDocs(q)
    const commits = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }))

    log.debug('Commits by author fetched', { count: commits.length })
    return commits
  } catch (error) {
    log.error('Failed to fetch commits by author', error)
    throw error
  }
}

/**
 * Get configured email recipients for commit notifications
 * @returns {Promise<{ emails: string[], disabledEmails: string[] }>}
 */
export async function getCommitEmailRecipients() {
  log.debug('Fetching commit email recipients')

  try {
    const db = getFirebaseDb()
    const docRef = doc(db, 'Utils', 'Commits')
    const docSnap = await getDoc(docRef)

    const data = docSnap.exists() ? docSnap.data() : {}
    const emails = data.emails || []
    const disabledEmails = data.disabledEmails || []
    log.debug('Email recipients fetched', { count: emails.length, disabled: disabledEmails.length })
    return { emails, disabledEmails }
  } catch (error) {
    log.error('Failed to fetch email recipients', error)
    throw error
  }
}

/**
 * Save email recipients for commit notifications
 * @param {string[]} emails - Array of email addresses
 * @param {string[]} disabledEmails - Array of disabled email addresses
 * @returns {Promise<void>}
 */
export async function saveCommitEmailRecipients(emails, disabledEmails = []) {
  log.info('Saving commit email recipients', {
    count: emails.length,
    disabled: disabledEmails.length,
  })

  try {
    const db = getFirebaseDb()
    const docRef = doc(db, 'Utils', 'Commits')
    await setDoc(docRef, { emails, disabledEmails }, { merge: true })
    log.info('Email recipients saved')
  } catch (error) {
    log.error('Failed to save email recipients', error)
    throw error
  }
}
